import React from 'react';
import Highlighter from 'react-highlight-words';

import { isEmpty, get } from 'lodash';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { AutoComplete, Spin, Tooltip } from 'antd';
import { RefSelectProps } from 'antd/lib/select';

import { faHotel, faIdBadge, faMapPin, faSearch, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { axiosInstanceB2B } from '@share/utils';
import { ClearSvg } from '@share/assets';

const { Option } = AutoComplete;

const LIST_HEIGHT = 500;
const MIN_SEARCH_LENGTH = 1;
const ZERO = 0;
const ONE = 1;

const MatchedByIcons = {
  propertyName: { icon: faHotel },
  leadName: { icon: faUser },
  bookingId: { icon: faIdBadge },
};

interface ITextSearch {
  value: string;
  matchedBy?: string;
  comments?: string;
  creditLineInvoiceId?: number | string;
}

interface IState {
  textsSearch: ITextSearch[];
  loading: boolean;
  isDropdownOpen: boolean;
  isFocused: boolean;
  textSearchLabel: string;
}

interface IProps extends WrappedComponentProps {
  setTextSearch: (item: any) => void;
  setTextSearchLabel: (label: string) => void;
  onRefresh: () => void;
  handleClear: () => void;
  options: string;
}

class CompanyStatementAutocompleteTextSearchComponent extends React.Component<IProps, IState> {
  state: IState = {
    textsSearch: [],
    loading: false,
    isDropdownOpen: false,
    isFocused: false,
    textSearchLabel: '',
  };

  selectRef: React.RefObject<RefSelectProps> = React.createRef();
  wrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
  lastSearch = '';

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps: any) {
    const { options } = this.props;
    if (prevProps.options !== options) {
      this.setState({
        isFocused: false,
        isDropdownOpen: false,
        textsSearch: [],
        textSearchLabel: '',
      });
      this.props.handleClear();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(event.target as Node)) {
      this.setState({ isFocused: false, isDropdownOpen: false, textsSearch: [] });
      this.lastSearch = '';
    }
  };

  onFocus = () => {
    const { isFocused, textSearchLabel } = this.state;
    if (!isFocused) {
      this.onSearch(textSearchLabel || '');
    }
    this.setState({ isFocused: true });
  };

  onSearch = async (search: string) => {
    this.lastSearch = search;

    if (search.length >= MIN_SEARCH_LENGTH) {
      this.setState({ loading: true });
      try {
        const updatedOptions = this.props?.options?.replace('From', 'To');
        const response = await axiosInstanceB2B.get(
          `/bookings/CreditLineInvoices?${this.props.options}=${search}&${updatedOptions}=${search}`,
        );
        this.setState({
          textsSearch: response.data.items || [],
          isDropdownOpen: true,
        });
      } finally {
        this.setState({ loading: false });
      }
    } else {
      this.setState({ textsSearch: [], isDropdownOpen: false });
    }
  };

  onChange = (value: string) => {
    this.setState({ textSearchLabel: value, isDropdownOpen: false });
  };

  onKeyDown = (e: React.KeyboardEvent) => {
    const { isDropdownOpen, isFocused, textsSearch } = this.state;
    if (e.key === 'Enter' && isDropdownOpen && isFocused && textsSearch.length === ONE) {
      const item = textsSearch[ZERO];
      this.setState({
        textSearchLabel: item.value,
        isDropdownOpen: false,
        textsSearch: [],
      });
    }
  };

  getNotFoundContent = () => {
    const { loading, textSearchLabel } = this.state;

    let message: React.ReactNode;
    if (textSearchLabel?.length < MIN_SEARCH_LENGTH) {
      message = <FormattedMessage id="search.min.symbols" />;
    } else if (loading) {
      message = <FormattedMessage id="searching" />;
    } else {
      message = <FormattedMessage id="reservation.text.search.nothing.found" />;
    }

    return <div className="locations-dropdown__nothing-found">{message}</div>;
  };

  getPopupContainer = () => {
    return this.wrapperRef ? this.wrapperRef.current : document.body;
  };

  onSelect = (event: any) => {
    const { textsSearch } = this.state;
    const item = textsSearch;
    this.setState({ isDropdownOpen: false, isFocused: true, textsSearch: [] }, () => {
      this.props.setTextSearch(item);
      this.props.onRefresh();
    });
  };

  handleClear = () => {
    this.props.handleClear();
  };

  render() {
    const { textsSearch, isDropdownOpen, textSearchLabel, loading } = this.state;
    const { options } = this.props;
    return (
      <div className="reservations-autocomplete-search__text-search" ref={this.wrapperRef}>
        {isEmpty(textSearchLabel) ? (
          <Tooltip
            placement="top"
            title={this.props.intl.formatMessage({ id: 'reservation.search.text' })}
          >
            <FontAwesomeIcon className="search-icon" icon={faSearch} />
          </Tooltip>
        ) : null}
        <AutoComplete
          onKeyDown={this.onKeyDown}
          showSearch
          placeholder={`Enter ${
            options === 'FromCreditLineInvoiceId'
              ? 'Invoice'
              : options === 'FromBookingId'
              ? 'Booking'
              : 'Amount'
          }`}
          onChange={this.onChange}
          onSearch={this.onSearch}
          onFocus={this.onFocus}
          ref={this.selectRef}
          open={isDropdownOpen}
          defaultActiveFirstOption={false}
          filterOption={false}
          getPopupContainer={this.getPopupContainer}
          listHeight={LIST_HEIGHT}
          allowClear
          value={textSearchLabel}
          notFoundContent={this.getNotFoundContent()}
          onSelect={this.onSelect}
          onClear={this.handleClear}
          clearIcon={<span>{loading ? <Spin /> : <ClearSvg />}</span>}
        >
          {textsSearch.map((text, index) => {
            const icon = get(MatchedByIcons, `${text.matchedBy}.icon`, faMapPin);
            return (
              <Option key={index} value={text.comments}>
                <span className="reservations-autocomplete-search__text-search-icon">
                  <FontAwesomeIcon icon={icon} />
                </span>
                <Highlighter
                  highlightClassName="bold-text"
                  searchWords={[this.lastSearch.trim()]}
                  autoEscape
                  textToHighlight={`${textSearchLabel} - ${text.comments?.toString()}`}
                />
              </Option>
            );
          })}
        </AutoComplete>
      </div>
    );
  }
}

export const CompanyStatementsAutocompleteTextSearch = injectIntl(
  CompanyStatementAutocompleteTextSearchComponent,
);
